import { useState, useEffect } from 'react';

export const useConfiguraciones = (mostrarNotificacion) => {
  const [usuario, setUsuario] = useState({});
  const [cargando, setCargando] = useState(false);
  const [toast, setToast] = useState({ open: false, mensaje: "", tipo: "" });
  
  // Estado de configuraciones con valores por defecto
  const [configuraciones, setConfiguraciones] = useState({
    // Notificaciones
    notificacionesEmail: true,
    notificacionesPush: true,
    sonidosNotificacion: false,
    
    // Privacidad
    autenticacionBiometrica: false,
    modoPrivado: false,
    
    // Apariencia
    tema: 'auto', // 'claro', 'oscuro', 'auto'
    efectosGlass: true,
    
    // Avanzado
    modoDesarrollador: false,
  });

  // Cargar configuraciones del localStorage al inicializar
  useEffect(() => {
    cargarConfiguraciones();
    cargarDatosUsuario();
  }, []);

  const cargarDatosUsuario = () => {
    try {
      const sesion = JSON.parse(sessionStorage.getItem("ppUL"));
      if (sesion) {
        setUsuario(sesion);
      }
    } catch (error) {
      console.error('Error cargando datos del usuario:', error);
    }
  };

  const cargarConfiguraciones = () => {
    try {
      const configGuardadas = localStorage.getItem('configuracionesPaciente');
      if (configGuardadas) {
        const config = JSON.parse(configGuardadas);
        setConfiguraciones(prevConfig => ({
          ...prevConfig,
          ...config
        }));
      }
    } catch (error) {
      console.error('Error cargando configuraciones:', error);
      mostrarNotificacion(true, 'Error al cargar configuraciones', 'error');
    }
  };

  const actualizarConfiguracion = (clave, valor) => {
    setConfiguraciones(prevConfig => ({
      ...prevConfig,
      [clave]: valor
    }));
    
    // Mostrar feedback visual
    mostrarNotificacion(true, 'Configuración actualizada', 'success');
    
    // Aplicar cambios inmediatos si es necesario
    aplicarCambioInmediato(clave, valor);
  };

  const aplicarCambioInmediato = (clave, valor) => {
    switch (clave) {
      case 'tema':
        aplicarTema(valor);
        break;
      case 'efectosGlass':
        aplicarEfectosGlass(valor);
        break;
      case 'modoDesarrollador':
        if (valor) {
          console.log('Modo desarrollador activado');
          mostrarNotificacion(true, 'Modo desarrollador activado', 'warning');
        }
        break;
      default:
        break;
    }
  };

  const aplicarTema = (tema) => {
    const root = document.documentElement;
    
    switch (tema) {
      case 'claro':
        root.setAttribute('data-theme', 'light');
        break;
      case 'oscuro':
        root.setAttribute('data-theme', 'dark');
        break;
      case 'auto':
        // Detectar preferencia del sistema
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        root.setAttribute('data-theme', mediaQuery.matches ? 'dark' : 'light');
        break;
      default:
        root.setAttribute('data-theme', 'light');
    }
  };

  const aplicarEfectosGlass = (habilitar) => {
    const root = document.documentElement;
    if (habilitar) {
      root.classList.add('glass-effects-enabled');
    } else {
      root.classList.remove('glass-effects-enabled');
    }
  };

  const guardarConfiguraciones = async () => {
    setCargando(true);
    
    try {
      // Simular guardado en servidor
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Guardar en localStorage
      localStorage.setItem('configuracionesPaciente', JSON.stringify(configuraciones));
      
      mostrarNotificacion(true, 'Configuraciones guardadas exitosamente', 'success');
    } catch (error) {
      console.error('Error guardando configuraciones:', error);
      mostrarNotificacion(true, 'Error al guardar configuraciones', 'error');
    } finally {
      setCargando(false);
    }
  };

  const resetearConfiguraciones = () => {
    const configDefault = {
      notificacionesEmail: true,
      notificacionesPush: true,
      sonidosNotificacion: false,
      autenticacionBiometrica: false,
      modoPrivado: false,
      tema: 'auto',
      efectosGlass: true,
      modoDesarrollador: false,
    };
    
    setConfiguraciones(configDefault);
    
    // Aplicar cambios inmediatos
    aplicarTema(configDefault.tema);
    aplicarEfectosGlass(configDefault.efectosGlass);
    
    mostrarNotificacion(true, 'Configuraciones restauradas a valores por defecto', 'warning');
  };

  const eliminarDatosLocales = () => {
    try {
      // Eliminar datos específicos (no toda la sesión)
      localStorage.removeItem('configuracionesPaciente');
      localStorage.removeItem('cacheTurnos');
      localStorage.removeItem('cacheEstudios');
      
      mostrarNotificacion(true, 'Datos locales eliminados', 'success');
    } catch (error) {
      console.error('Error eliminando datos locales:', error);
      mostrarNotificacion(true, 'Error al eliminar datos locales', 'error');
    }
  };

  const exportarConfiguraciones = () => {
    try {
      const dataStr = JSON.stringify(configuraciones, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'configuraciones-paciente.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      mostrarNotificacion(true, 'Configuraciones exportadas', 'success');
    } catch (error) {
      console.error('Error exportando configuraciones:', error);
      mostrarNotificacion(true, 'Error al exportar configuraciones', 'error');
    }
  };

  return {
    usuario,
    setUsuario,
    configuraciones,
    cargando,
    setCargando,
    toast,
    setToast,
    actualizarConfiguracion,
    guardarConfiguraciones,
    resetearConfiguraciones,
    eliminarDatosLocales,
    exportarConfiguraciones,
    cargarConfiguraciones,
  };
};